import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useChat } from "./ChatContext";
import useAuth from "../hooks/useAuth";
import socketService from "../services/socketService";

const CallContext = createContext();

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within CallProvider");
  }
  return context;
};

// WebRTC ICE Servers (Free Google STUN servers)
const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
  ],
};

export const CallProvider = ({ children }) => {
  const { socket, currentUserId } = useChat();
  const { user: authUser } = useAuth();

  const [callState, setCallState] = useState("idle"); // 'idle' | 'outgoing' | 'incoming' | 'connected'
  const [callType, setCallType] = useState("video"); // 'video' | 'voice'
  const [targetUser, setTargetUser] = useState(null); // { id, name, avatar }
  const [incomingCallData, setIncomingCallData] = useState(null); // { callerId, callerName, callerAvatar, isVideo, signalData }
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const ringtoneIntervalRef = useRef(null);

  // Helper to obtain current active socket
  const getActiveSocket = useCallback(() => {
    return socket || socketService.getSocket();
  }, [socket]);

  // Play synthetic pleasant ringtone using Web Audio API
  const startRingtone = useCallback((isOutgoing = false) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") {
        ctx.resume();
      }

      const playTone = () => {
        if (!ctx || ctx.state === "closed") return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(isOutgoing ? 440 : 520, ctx.currentTime);
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (isOutgoing ? 1.2 : 0.8));
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + (isOutgoing ? 1.2 : 0.8));
      };

      playTone();
      ringtoneIntervalRef.current = setInterval(playTone, isOutgoing ? 3000 : 2000);
    } catch (e) {
      console.warn("Could not start ringtone audio:", e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
  }, []);

  // Cleanup helper
  const cleanUpCall = useCallback(() => {
    stopRingtone();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (err) {
        console.warn("Error closing peer connection:", err);
      }
      peerConnectionRef.current = null;
    }

    remoteStreamRef.current = null;
    setCallState("idle");
    setTargetUser(null);
    setIncomingCallData(null);
    setIsMuted(false);
    setIsVideoOff(false);
    setIsScreenSharing(false);
    setCallDuration(0);
  }, [stopRingtone]);

  // Start duration timer
  const startTimer = useCallback(() => {
    setCallDuration(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  // Initialize WebRTC Peer Connection
  const createPeerConnection = useCallback(
    (targetUserId, conversationId) => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnectionRef.current = pc;

      // Add local stream tracks to PeerConnection
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          pc.addTrack(track, localStreamRef.current);
        });
      }

      // Handle incoming remote tracks
      pc.ontrack = (event) => {
        console.log("[WEBRTC] 📹 Received remote track:", event.streams[0]);
        if (event.streams && event.streams[0]) {
          remoteStreamRef.current = event.streams[0];
          setCallState("connected");
        }
      };

      // Handle ICE Candidate generation
      pc.onicecandidate = (event) => {
        const curSocket = getActiveSocket();
        if (event.candidate && curSocket) {
          curSocket.emit("call:signal", {
            targetUserId,
            signalData: { type: "candidate", candidate: event.candidate },
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log("[WEBRTC] Connection state:", pc.connectionState);
        if (pc.connectionState === "connected") {
          stopRingtone();
          setCallState("connected");
          startTimer();
        } else if (
          pc.connectionState === "disconnected" ||
          pc.connectionState === "failed" ||
          pc.connectionState === "closed"
        ) {
          cleanUpCall();
        }
      };

      return pc;
    },
    [getActiveSocket, stopRingtone, startTimer, cleanUpCall]
  );

  // 1. Initiate an Outgoing Call
  const startCall = useCallback(
    async (recipientUser, isVideo = true, conversationId = null) => {
      const curSocket = getActiveSocket();

      let targetId = null;
      let targetName = "User";
      let targetAvatar = null;

      if (typeof recipientUser === "string") {
        targetId = recipientUser;
      } else if (recipientUser && typeof recipientUser === "object") {
        targetId = recipientUser.id || recipientUser.userId || recipientUser._id;
        targetName =
          `${recipientUser.firstName || recipientUser.first_name || ""} ${
            recipientUser.lastName || recipientUser.last_name || ""
          }`.trim() ||
          recipientUser.name ||
          recipientUser.email ||
          "User";
        targetAvatar = recipientUser.avatar || recipientUser.image || recipientUser.profileImage || null;
      }

      if (!curSocket) {
        console.warn("Cannot start call: socket instance is not available");
        alert("Live chat connection is still initializing. Please wait a few seconds and try again.");
        return;
      }

      if (!targetId) {
        console.warn("Cannot start call: recipient ID is missing", recipientUser);
        alert("Please select a direct conversation with a specific contact to start a call.");
        return;
      }

      try {
        setCallType(isVideo ? "video" : "voice");
        setTargetUser({ id: targetId, name: targetName, avatar: targetAvatar });
        setCallState("outgoing");
        startRingtone(true);

        // Get User Media
        const stream = await navigator.mediaDevices.getUserMedia({
          video: isVideo ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
          audio: true,
        });
        localStreamRef.current = stream;

        const pc = createPeerConnection(targetId, conversationId);

        // Create Offer
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: isVideo,
        });
        await pc.setLocalDescription(offer);

        const myName =
          `${authUser?.first_name || authUser?.firstName || ""}`.trim() ||
          authUser?.name ||
          authUser?.email ||
          "User";
        const myAvatar = authUser?.image || authUser?.avatar || null;

        // Emit call initiation via Socket
        curSocket.emit("call:initiate", {
          recipientId: targetId,
          conversationId,
          isVideo,
          signalData: offer,
          callerInfo: {
            id: currentUserId || authUser?.id,
            name: myName,
            avatar: myAvatar,
          },
        });
      } catch (err) {
        console.error("Error starting call:", err);
        cleanUpCall();
        alert(
          err.name === "NotAllowedError"
            ? "Camera/Microphone permission was denied. Please allow camera and mic permissions in browser settings."
            : "Failed to access camera/microphone: " + err.message
        );
      }
    },
    [getActiveSocket, authUser, currentUserId, createPeerConnection, startRingtone, cleanUpCall]
  );

  // 2. Accept an Incoming Call
  const acceptCall = useCallback(async () => {
    const curSocket = getActiveSocket();
    if (!incomingCallData || !curSocket) return;
    stopRingtone();

    try {
      const isVideo = incomingCallData.isVideo;
      setCallType(isVideo ? "video" : "voice");
      setTargetUser({
        id: incomingCallData.callerId,
        name: incomingCallData.callerName,
        avatar: incomingCallData.callerAvatar,
      });

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideo ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
        audio: true,
      });
      localStreamRef.current = stream;

      const pc = createPeerConnection(incomingCallData.callerId, incomingCallData.conversationId);

      // Set Remote Description from caller's offer
      if (incomingCallData.signalData) {
        await pc.setRemoteDescription(new RTCSessionDescription(incomingCallData.signalData));
      }

      // Create Answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // Emit accept to caller
      curSocket.emit("call:accept", {
        callerId: incomingCallData.callerId,
        conversationId: incomingCallData.conversationId,
        signalData: answer,
      });

      setCallState("connected");
      startTimer();
    } catch (err) {
      console.error("Error accepting call:", err);
      cleanUpCall();
      alert("Failed to access camera/microphone: " + err.message);
    }
  }, [incomingCallData, getActiveSocket, stopRingtone, createPeerConnection, startTimer, cleanUpCall]);

  // 3. Reject an Incoming Call
  const rejectCall = useCallback(
    (reason = "declined") => {
      const curSocket = getActiveSocket();
      if (incomingCallData && curSocket) {
        curSocket.emit("call:reject", {
          callerId: incomingCallData.callerId,
          conversationId: incomingCallData.conversationId,
          reason,
        });
      }
      cleanUpCall();
    },
    [incomingCallData, getActiveSocket, cleanUpCall]
  );

  // 4. End Call
  const endCall = useCallback(() => {
    const curSocket = getActiveSocket();
    if (curSocket && targetUser?.id) {
      curSocket.emit("call:end", {
        targetUserId: targetUser.id,
      });
    }
    cleanUpCall();
  }, [getActiveSocket, targetUser, cleanUpCall]);

  // 5. Toggle Mic Mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // 6. Toggle Camera Video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  }, []);

  // 7. Toggle Screen Sharing
  const toggleScreenShare = useCallback(async () => {
    if (!peerConnectionRef.current) return;

    if (!isScreenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];

        const sender = peerConnectionRef.current.getSenders().find((s) => s.track && s.track.kind === "video");
        if (sender) {
          sender.replaceTrack(screenTrack);
        }

        screenTrack.onended = () => {
          // Revert back to webcam track
          if (localStreamRef.current) {
            const webcamTrack = localStreamRef.current.getVideoTracks()[0];
            if (sender && webcamTrack) {
              sender.replaceTrack(webcamTrack);
            }
          }
          setIsScreenSharing(false);
        };

        setIsScreenSharing(true);
      } catch (err) {
        console.error("Screen share error:", err);
      }
    } else {
      // Switch back to webcam
      if (localStreamRef.current) {
        const webcamTrack = localStreamRef.current.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find((s) => s.track && s.track.kind === "video");
        if (sender && webcamTrack) {
          sender.replaceTrack(webcamTrack);
        }
      }
      setIsScreenSharing(false);
    }
  }, [isScreenSharing]);

  // Listen to Socket Signaling Events
  useEffect(() => {
    const curSocket = getActiveSocket();
    if (!curSocket) return;

    // Incoming Call Received
    const handleIncoming = (data) => {
      console.log("[CALL] 📞 Incoming call:", data);
      if (callState !== "idle") {
        curSocket.emit("call:reject", {
          callerId: data.callerId,
          conversationId: data.conversationId,
          reason: "busy",
        });
        return;
      }

      setIncomingCallData(data);
      setCallType(data.isVideo ? "video" : "voice");
      setCallState("incoming");
      startRingtone(false);
    };

    // Call Accepted by recipient
    const handleAccepted = async (data) => {
      console.log("[CALL] ✅ Call accepted by recipient:", data);
      stopRingtone();
      if (peerConnectionRef.current && data.signalData) {
        try {
          await peerConnectionRef.current.setRemoteDescription(
            new RTCSessionDescription(data.signalData)
          );
          setCallState("connected");
          startTimer();
        } catch (e) {
          console.error("Error setting remote description on accepted call:", e);
        }
      }
    };

    // Call Rejected
    const handleRejected = (data) => {
      console.log("[CALL] ❌ Call rejected:", data);
      stopRingtone();
      alert(`Call was ${data.reason === "busy" ? "busy" : "declined"}.`);
      cleanUpCall();
    };

    // Recipient Offline
    const handleUserOffline = () => {
      console.log("[CALL] 📴 Recipient is offline");
      stopRingtone();
      alert("User is currently offline.");
      cleanUpCall();
    };

    // WebRTC ICE candidate / signal received
    const handleSignal = async (data) => {
      if (peerConnectionRef.current && data.signalData) {
        try {
          if (data.signalData.type === "candidate" && data.signalData.candidate) {
            await peerConnectionRef.current.addIceCandidate(
              new RTCIceCandidate(data.signalData.candidate)
            );
          } else if (data.signalData.type === "offer") {
            await peerConnectionRef.current.setRemoteDescription(
              new RTCSessionDescription(data.signalData)
            );
            const answer = await peerConnectionRef.current.createAnswer();
            await peerConnectionRef.current.setLocalDescription(answer);
            curSocket.emit("call:signal", {
              targetUserId: data.senderId,
              signalData: answer,
            });
          } else if (data.signalData.type === "answer") {
            await peerConnectionRef.current.setRemoteDescription(
              new RTCSessionDescription(data.signalData)
            );
          }
        } catch (e) {
          console.error("Error processing signal data:", e);
        }
      }
    };

    // Call Ended by other user
    const handleEnded = () => {
      console.log("[CALL] ⏹️ Remote user ended call");
      cleanUpCall();
    };

    curSocket.on("call:incoming", handleIncoming);
    curSocket.on("call:accepted", handleAccepted);
    curSocket.on("call:rejected", handleRejected);
    curSocket.on("call:user-offline", handleUserOffline);
    curSocket.on("call:signal", handleSignal);
    curSocket.on("call:ended", handleEnded);

    return () => {
      curSocket.off("call:incoming", handleIncoming);
      curSocket.off("call:accepted", handleAccepted);
      curSocket.off("call:rejected", handleRejected);
      curSocket.off("call:user-offline", handleUserOffline);
      curSocket.off("call:signal", handleSignal);
      curSocket.off("call:ended", handleEnded);
    };
  }, [socket, callState, getActiveSocket, startRingtone, stopRingtone, startTimer, cleanUpCall]);

  return (
    <CallContext.Provider
      value={{
        callState,
        callType,
        targetUser,
        incomingCallData,
        localStream: localStreamRef.current,
        remoteStream: remoteStreamRef.current,
        isMuted,
        isVideoOff,
        isScreenSharing,
        callDuration,
        startCall,
        acceptCall,
        rejectCall,
        endCall,
        toggleMute,
        toggleVideo,
        toggleScreenShare,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};
